import React, { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Button, IconButton, MenuItem, Paper, TextField, Typography } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import { Steps, SubcontractStatusRule, SUBCONTRACT_RULE_STATUSES } from 'models/workflow-step';

type PartnerRulesEditorProps = {
    partnerName: string;
    steps: Steps[];
    rules: SubcontractStatusRule[];
    isSaving: boolean;
    onSave: (rules: SubcontractStatusRule[]) => void;
};

const sameRules = (left: SubcontractStatusRule[], right: SubcontractStatusRule[]) =>
    JSON.stringify(left) === JSON.stringify(right);

/**
 * DD-3489: one connected partner's rules — "when this partner's sub-order reaches
 * <status>, move my order to <my step>". One rule per status.
 */
const PartnerRulesEditor: FC<PartnerRulesEditorProps> = ({ partnerName, steps, rules, isSaving, onSave }) => {
    const { t: translate } = useTranslation(['workflow', 'orders', 'common']);
    const [draftRules, setDraftRules] = useState<SubcontractStatusRule[]>(rules);

    useEffect(() => setDraftRules(rules), [rules]);

    const usedStatuses = draftRules.map((rule) => rule.status);
    const unusedStatuses = SUBCONTRACT_RULE_STATUSES.filter((status) => !usedStatuses.includes(status));
    const isComplete = draftRules.every((rule) => rule.status && rule.stepId);

    const updateRule = (ruleIndex: number, changes: Partial<SubcontractStatusRule>) =>
        setDraftRules(draftRules.map((rule, index) => (index === ruleIndex ? { ...rule, ...changes } : rule)));

    const removeRule = (ruleIndex: number) => setDraftRules(draftRules.filter((_, index) => index !== ruleIndex));

    const addRule = () => setDraftRules([...draftRules, { status: unusedStatuses[0], stepId: '' }]);

    return (
        <Paper variant="outlined" sx={{ p: 2, mb: 2 }} data-test="subcontractor-rules.partner">
            <Typography variant="subtitle2" mb={1}>
                {partnerName}
            </Typography>
            {draftRules.map((rule, ruleIndex) => (
                <Box key={ruleIndex} display="flex" alignItems="center" gap={2} mb={1} flexWrap="wrap">
                    <TextField
                        select
                        size="small"
                        sx={{ minWidth: 220 }}
                        label={translate('workflow:SUB_ORDER_REACHES')}
                        value={rule.status}
                        onChange={(event) => updateRule(ruleIndex, { status: event.target.value })}
                    >
                        {SUBCONTRACT_RULE_STATUSES.filter(
                            (status) => status === rule.status || !usedStatuses.includes(status),
                        ).map((status) => (
                            <MenuItem key={status} value={status}>
                                {translate(`orders:${status}`)}
                            </MenuItem>
                        ))}
                    </TextField>
                    <TextField
                        select
                        size="small"
                        sx={{ minWidth: 220 }}
                        label={translate('workflow:MOVE_MY_ORDER_TO')}
                        value={rule.stepId}
                        onChange={(event) => updateRule(ruleIndex, { stepId: event.target.value })}
                    >
                        {steps.map((step) => (
                            <MenuItem key={step._id} value={step._id}>
                                {step.name}
                            </MenuItem>
                        ))}
                    </TextField>
                    <IconButton aria-label={translate('common:DELETE')} onClick={() => removeRule(ruleIndex)}>
                        <DeleteIcon />
                    </IconButton>
                </Box>
            ))}
            <Box display="flex" justifyContent="space-between" mt={1}>
                <Button startIcon={<AddIcon />} disabled={!unusedStatuses.length} onClick={addRule}>
                    {translate('workflow:ADD_SUBCONTRACTOR_RULE')}
                </Button>
                <Button
                    variant="contained"
                    disabled={isSaving || !isComplete || sameRules(draftRules, rules)}
                    onClick={() => onSave(draftRules)}
                >
                    {translate('common:SAVE')}
                </Button>
            </Box>
        </Paper>
    );
};

export default PartnerRulesEditor;
